import Button from '@/common/components/Button'
import { DateInput } from '@/common/components/DateInput/DateInput'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateContractClosingDate } from '@/features/privateCompanies/hooks/contract/useUpdateContractClosingDate'
import {
  updateContractClosingDateSchema,
  UpdateContractClosingDateSchema,
} from '@/features/privateCompanies/schemas/contract/updateContractClosingDate.schema'
import { CompanyContractClosingDateType } from '@/features/privateCompanies/types/companyContractClosingDate.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon, PencilIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface UpdateClosingDateModalProps {
  contractId?: string
  contractClosingDate?: CompanyContractClosingDateType
}

export const UpdateClosingDateModal = ({ contractId, contractClosingDate }: UpdateClosingDateModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateContractClosingDateSchema>({
    resolver: zodResolver(updateContractClosingDateSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['clientContract', contractId] })
    queryClient.invalidateQueries({ queryKey: ['companyContractClosingDate', contractId] })
    setIsOpen(false)
  }

  const { mutate: updateContractClosingDate, isPending: updateContractClosingDateIsPending } =
    useUpdateContractClosingDate({
      contractId: contractId,
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractClosingDateSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractClosingDateSchema> = (data) => {
    updateContractClosingDate(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        closing_date: contractClosingDate?.next_closing_date
          ? dateTransformer(contractClosingDate.next_closing_date)
          : '',
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_contract_closing_date_modal"
          operationKlassToCheckPermission="Admin::Client::Contracts::UpdateClosingDate"
          variant="link"
          className="p-1"
        >
          <PencilIcon strokeWidth={1} className="size-4" />
        </Button>
      }
      title="Editando data de fechamento da próxima fatura"
      dataTestid="update_contract_closing_date"
      bodyClassName="max-h-[calc(100vh-10rem)] overflow-y-auto"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractClosingDateIsPending}
      confirmButtonIsLoading={updateContractClosingDateIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <DateInput
              fieldName="closing_date"
              control={control}
              errors={errors}
              label="Data de fechamento"
              markAsRequired
              disabled={updateContractClosingDateIsPending}
            />

            <div className="flex items-center gap-x-2">
              <InfoIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[0.625rem] leading-tight font-light italic">
                Estas mudanças so podem ser feitas dentro de uma janela de manutenção, das 8 da manha ate as 8 da noite
              </p>
            </div>
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
